import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CashbackFacade } from '@features/cashback/facades/cashback.facade';
import { EnrollmentFacade } from '@features/enrollment/facades/enrollment.facade';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { MembershipFacade } from '@features/membership/facades/membership.facade';
import { Store } from '@ngrx/store';
import { getAuthUser } from '@rd/core/auth/store/selectors/auth.selectors';
import { TENANT_NAME } from '@rd/core/tokens/rd-lib.token';
import {
  CashBackMembership,
  CashBackToAccountMembership,
  CashBackToCardMembership,
  Enrollment,
  LoyaltyProgram,
  LoyaltyProgramCategory,
  MembershipType,
  User,
} from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import {
  ANZEnrollmentFormComponentComponent,
  EnrollmentFormOptions,
} from '@rd/wl/anz/components/enrollment-form-component/enrollment-form.component';
import { EnrollmentFormType } from '@rd/wl/anz/types';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, startWith, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'rd-anz-rcv2-enrollment',
  templateUrl: './enrollment.component.html',
  styleUrls: ['./enrollment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AnzRCEnrollmentComponent implements OnInit, OnDestroy {
  get formType$(): Observable<EnrollmentFormType> {
    return this.component.formType$;
  }

  get form(): UntypedFormGroup {
    return this.component.form;
  }

  get enrollmentForm(): UntypedFormGroup {
    return this.form.controls.enrollment as UntypedFormGroup;
  }

  get bankAccountForm(): UntypedFormGroup {
    return this.form.controls.accountDetails as UntypedFormGroup;
  }

  get selectedOption(): UntypedFormControl {
    return this.form.get('selectedOption') as UntypedFormControl;
  }

  constructor(
    @Inject(TENANT_NAME) private tenantName: string,
    public membershipFacade: MembershipFacade,
    public loyaltyProgramFacade: LoyaltyProgramFacade,
    public enrollmentFacade: EnrollmentFacade,
    private store: Store,
    private cashbackFacade: CashbackFacade,
  ) {}
  @ViewChild(ANZEnrollmentFormComponentComponent, { static: true })
  component: ANZEnrollmentFormComponentComponent;

  enrollmentFormType = EnrollmentFormType;

  destroy$: Subject<void> = new Subject();

  disableButtonState$: Observable<boolean> =
    this.membershipFacade.createOrUpdateMembershipLoading$;

  loyaltyProgram$ = new BehaviorSubject<LoyaltyProgram | null>(null);

  cashBackToCardLoyaltyProgram$ =
    this.loyaltyProgramFacade.cashBackToCardLoyaltyProgram$.pipe(
      filter(isNotNull),
    );

  // Don't filter by null since ANZ Business doesn't have a cashback to account
  // loyalty program
  cashBackToAccountLoyaltyProgram$: Observable<LoyaltyProgram | null> =
    this.loyaltyProgramFacade.cashBackToAccountLoyaltyProgram$;

  cashBackToAccountMembership$: Observable<CashBackToAccountMembership | null> =
    this.membershipFacade.cashBackToAccountMembership$;

  cashBackToCardMembership$: Observable<CashBackToCardMembership | null> =
    this.membershipFacade.cashBackToCardMembership$;

  checkoutFormData$ = this.cashbackFacade.cashbackForm$.pipe(filter(isNotNull));
  user$: Observable<User> = this.store
    .select(getAuthUser)
    .pipe(filter(isNotNull));

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loyaltyProgram$.complete();
    this.membershipFacade.clearMembershipErrors();
  }

  ngOnInit(): void {
    combineLatest([
      this.selectedOption.valueChanges.pipe(
        startWith(this.selectedOption.value),
        filter(isNotNull),
      ),
      this.cashBackToAccountLoyaltyProgram$,
      this.cashBackToCardLoyaltyProgram$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        tap(([option, cashBackToAccountLp, cashBackToCardLp]) => {
          switch (option) {
            case EnrollmentFormOptions.BankAccount: {
              this.loyaltyProgram$.next(cashBackToAccountLp);
              break;
            }
            case EnrollmentFormOptions.CreditCard: {
              this.loyaltyProgram$.next(cashBackToCardLp);
              break;
            }
          }
        }),
      )
      .subscribe();

    combineLatest([
      this.membershipFacade.preferredCashBackMembership$,
      this.enrollmentFacade.enrollments$,
      this.formType$,
      this.user$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        tap(
          ([preferredCashBackMembership, enrollments, formType, user]: [
            CashBackMembership | null,
            Enrollment[],
            EnrollmentFormType,
            User,
          ]) => {
            // Check if there are enrollments, if there are, set default for now
            // will be overridden in next checks
            if (enrollments.length > 0) {
              this.component.form.patchValue({
                enrollment: {
                  selected: enrollments[0],
                },
              });
            }

            // Cashback to card membership exists and a user has enrollments,
            // prefill selected enrollment and prefill username from token
            if (
              this.isMembershipCashBackToCard(preferredCashBackMembership) &&
              enrollments.length > 0
            ) {
              const selectedEnrollment = enrollments.find(
                (enrollment) =>
                  enrollment.id === preferredCashBackMembership.enrollmentId,
              );
              return this.component.form.patchValue({
                selectedOption: this.component.formOptions.CreditCard,
                enrollment: {
                  selected: selectedEnrollment ?? enrollments[0],
                },
                // first name and last name might be null coming from token
                accountDetails: {
                  accountFirstName: user.firstName ?? '',
                  accountLastName: user.lastName ?? '',
                },
              });
            }
            // cashback to account membership exists, prefill account info from memberhsip
            if (
              this.isMembershipCashBackToAccount(preferredCashBackMembership)
            ) {
              return this.component.form.patchValue({
                selectedOption: this.component.formOptions.BankAccount,
                accountDetails: {
                  accountFirstName: preferredCashBackMembership.firstName,
                  accountLastName: preferredCashBackMembership.lastName,
                  accountNumber: preferredCashBackMembership.number,
                  bsbNumber: preferredCashBackMembership.details.bsbNumber,
                },
              });
            }

            // No memberships, pre-select correct option based on child component's formType$
            switch (formType) {
              case EnrollmentFormType.CreditCardAndBankAccount: {
                return this.component.form.patchValue({
                  selectedOption: this.component.formOptions.CreditCard,
                });
              }

              case EnrollmentFormType.CreditCardOnly: {
                return this.component.form.patchValue({
                  selectedOption: this.component.formOptions.CreditCard,
                });
              }

              case EnrollmentFormType.NoCreditCardOnlyBankAccount: {
                return this.component.form.patchValue({
                  selectedOption: this.component.formOptions.BankAccount,
                  accountDetails: {
                    accountFirstName: user.firstName ?? '',
                    accountLastName: user.lastName ?? '',
                  },
                });
              }
            }
          },
        ),
      )
      .subscribe();
  }

  submit(): void {
    const selectedOption: EnrollmentFormOptions = this.selectedOption.value;
    switch (selectedOption) {
      case EnrollmentFormOptions.CreditCard: {
        this.enrollmentForm.markAllAsTouched();
        this.createOrUpdateMembership(LoyaltyProgramCategory.CashbackToCard);
        break;
      }
      case EnrollmentFormOptions.BankAccount: {
        this.bankAccountForm.markAllAsTouched();
        if (this.bankAccountForm.valid) {
          this.createOrUpdateMembership(
            LoyaltyProgramCategory.CashbackToAccount,
          );
        }
        break;
      }
    }
  }

  private createOrUpdateMembership(
    membershipCategory:
      | LoyaltyProgramCategory.CashbackToCard
      | LoyaltyProgramCategory.CashbackToAccount,
  ): void {
    if (membershipCategory === LoyaltyProgramCategory.CashbackToCard) {
      const enrollment: Enrollment = this.enrollmentForm.value.selected;
      combineLatest(
        this.cashBackToCardMembership$,
        this.cashBackToCardLoyaltyProgram$,
      )
        .pipe(
          take(1),
          tap(([membership, loyaltyProgram]) => {
            if (membership === null) {
              this.membershipFacade.createMembership({
                type: MembershipType.CashBack,
                membership: {
                  loyalty_program_id: loyaltyProgram?.id,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  enrollmentId: enrollment.id!,
                  number: enrollment.groupReferenceId,
                },
              });
            } else {
              this.membershipFacade.updateMembership({
                type: MembershipType.CashBack,
                membershipId: membership?.id,
                membership: {
                  number: enrollment.groupReferenceId,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  enrollmentId: enrollment.id!,
                },
              });
            }
          }),
        )
        .subscribe();
      return;
    }

    // Cashback to account
    const bankform = this.bankAccountForm.value;
    combineLatest(
      this.cashBackToAccountMembership$,
      this.cashBackToAccountLoyaltyProgram$,
    )
      .pipe(
        take(1),
        tap(([membership, loyaltyProgram]) => {
          if (membership === null) {
            this.membershipFacade.createMembership({
              type: MembershipType.CashBack,
              membership: {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                loyalty_program_id: loyaltyProgram?.id,
                number: bankform.accountNumber,
                firstName: bankform.accountFirstName,
                lastName: bankform.accountLastName,
                details: {
                  bsb_number: bankform.bsbNumber,
                },
              },
            });
          } else {
            this.membershipFacade.updateMembership({
              type: MembershipType.CashBack,
              membershipId: membership?.id,
              membership: {
                number: bankform.accountNumber,
                firstName: bankform.accountFirstName,
                lastName: bankform.accountLastName,
                details: {
                  bsb_number: bankform.bsbNumber,
                },
              },
            });
          }
        }),
      )
      .subscribe();
  }

  private isMembershipCashBackToCard(
    membership: CashBackMembership | null,
  ): membership is CashBackToCardMembership {
    return (
      membership !== null &&
      membership.type === MembershipType.CashBack &&
      membership.category === LoyaltyProgramCategory.CashbackToCard
    );
  }

  private isMembershipCashBackToAccount(
    membership: CashBackMembership | null,
  ): membership is CashBackToAccountMembership {
    return (
      membership !== null &&
      membership.type === MembershipType.CashBack &&
      membership.category === LoyaltyProgramCategory.CashbackToAccount
    );
  }
}
